import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getNotificationsForProvider,
  markNotificationRead,
} from "../APIs/referralApi";

const TYPE_COLORS = {
  REFERRAL_CREATED: "text-blue-700",
  REFERRAL_ACCEPTED: "text-green-700",
  REFERRAL_DECLINED: "text-red-600",
  APPOINTMENT_OFFERED: "text-blue-700",
  APPOINTMENT_BOOKED: "text-green-700",
  VISIT_COMPLETED: "text-gray-600",
  MANUAL_REVIEW: "text-orange-700",
};

export default function NotificationsTab({ providerId }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    if (!providerId) return;
    try {
      const data = await getNotificationsForProvider(providerId);
      setNotifications(Array.isArray(data) ? data : (data.content ?? []));
      setError("");
    } catch (err) {
      // Surface error only on first load, silently retry after
      if (loading) setError("Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, [providerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  // Reload on tab focus
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const handleClick = async (n) => {
    if (n.status === "READ") return;
    try {
      await markNotificationRead(n.id ?? n.notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          (item.id ?? item.notificationId) === (n.id ?? n.notificationId)
            ? { ...item, status: "READ" }
            : item,
        ),
      );
    } catch {
      // non-critical
    }
  };

  const unreadCount = notifications.filter((n) => n.status !== "READ").length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-xl font-semibold">Notifications</h3>
        {unreadCount > 0 && (
          <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 font-bold">
            {unreadCount}
          </span>
        )}
      </div>

      {loading && (
        <p className="text-sm text-gray-500 py-4 text-center">
          Loading notifications...
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600 text-center py-4">{error}</p>
      )}

      <div className="space-y-3 p-1">
        {!loading && notifications.length === 0 && !error && (
          <p className="text-gray-500 text-sm text-center py-6">
            No notifications.
          </p>
        )}
        {notifications.map((n) => {
          const id = n.id ?? n.notificationId;
          const isUnread = n.status !== "READ";
          const typeCls = TYPE_COLORS[n.type] ?? "text-blue-700";
          return (
            <div
              key={id}
              onClick={() => handleClick(n)}
              className={`border rounded p-4 shadow-sm cursor-pointer transition ${
                isUnread
                  ? "bg-white border-blue-100"
                  : "bg-gray-50 border-gray-200 opacity-70"
              }`}
            >
              {isUnread && (
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2 align-middle" />
              )}
              <span className={`font-semibold text-sm ${typeCls}`}>
                {n.type?.replace(/_/g, " ")}
              </span>
              <p className="mt-1 text-gray-800 text-sm">{n.message}</p>
              {n.createdAt && (
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(n.createdAt).toLocaleString()}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
